import React, { useState, useEffect, Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { supabase } from './supabase';
import './App.css';

const KaraokeHome = lazy(() => import('./components/KaraokeHome'));
const VideoPlayer = lazy(() => import('./components/VideoPlayer'));

const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0a0a0c]">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full shadow-[0_0_15px_rgba(34,211,238,0.5)]"></div>
      <p className="text-cyan-400 font-mono text-xs tracking-[0.3em] animate-pulse">WARMING UP TUBES...</p>
    </div>
  </div>
);

function App() {
  const [playlist, setPlaylist] = useState([]);
  const [currentShareId, setCurrentShareId] = useState(null);

  useEffect(() => {
    if (!supabase || !currentShareId) return;

    const fetchPlaylist = async () => {
      const { data } = await supabase
        .from('shared_playlists_1740645600000')
        .select('songs')
        .eq('id', currentShareId)
        .single();
      if (data) setPlaylist(data.songs);
    };

    fetchPlaylist();

    const channel = supabase
      .channel(`playlist-${currentShareId}`)
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'shared_playlists_1740645600000',
        filter: `id=eq.${currentShareId}`
      }, (payload) => {
        if (payload.new && payload.new.songs) {
          setPlaylist(payload.new.songs);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentShareId]);

  const updateRemotePlaylist = async (newPlaylist) => {
    if (!supabase || !currentShareId) return;
    await supabase
      .from('shared_playlists_1740645600000')
      .update({ songs: newPlaylist, updated_at: new Date() })
      .eq('id', currentShareId);
  };

  const addToPlaylist = async (song) => {
    setPlaylist(prev => {
      if (prev.find(s => s.videoId === song.videoId)) return prev;
      const updated = [...prev, { ...song, id: Date.now() + Math.random() }];
      if (currentShareId) updateRemotePlaylist(updated);
      return updated;
    });
  };

  const removeFromPlaylist = async (id) => {
    setPlaylist(prev => {
      const updated = prev.filter(song => song.id !== id);
      if (currentShareId) updateRemotePlaylist(updated);
      return updated;
    });
  };

  const createSharedSession = async () => {
    if (!supabase) {
      alert("Please connect to Supabase to enable sharing features!");
      return;
    }
    const { data } = await supabase
      .from('shared_playlists_1740645600000')
      .insert({ songs: playlist })
      .select()
      .single();
    if (data) setCurrentShareId(data.id);
  };

  return (
    <Router>
      <div className="min-h-screen bg-[#0a0a0c]">
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={
              <KaraokeHome 
                playlist={playlist} 
                addToPlaylist={addToPlaylist} 
                removeFromPlaylist={removeFromPlaylist} 
                shareId={currentShareId} 
                onShare={createSharedSession}
                setShareId={setCurrentShareId}
              />
            } />
            <Route path="/:shareId" element={
              <KaraokeHome 
                playlist={playlist} 
                addToPlaylist={addToPlaylist} 
                removeFromPlaylist={removeFromPlaylist} 
                shareId={currentShareId} 
                onShare={createSharedSession}
                setShareId={setCurrentShareId}
              />
            } />
            <Route path="/video/:videoId" element={
              <VideoPlayer 
                playlist={playlist} 
                addToPlaylist={addToPlaylist}
                removeFromPlaylist={removeFromPlaylist} 
                currentShareId={currentShareId} 
              />
            } />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;